/**
 * 七日充值活动 V2 版本工具函数
 */

import type {
  DailyRechargeTask,
  DailyRechargeItem,
} from "./SevenDayRecharge.types";
import Chest01Open from "@/assets/sevenDayRecharge/01_open.webp";
import Chest02Open from "@/assets/sevenDayRecharge/02_open.webp";
import Chest03Open from "@/assets/sevenDayRecharge/03_open.webp";
import Chest04Open from "@/assets/sevenDayRecharge/04_open.webp";
import Chest01Close from "@/assets/sevenDayRecharge/01_close.webp";
import Chest02Close from "@/assets/sevenDayRecharge/02_close.webp";
import Chest03Close from "@/assets/sevenDayRecharge/03_close.webp";
import Chest04Close from "@/assets/sevenDayRecharge/04_close.webp";

/**
 * V2 进度数据结构
 */
export interface V2ProgressData {
  currentProgress: number;
  totalProgress: number;
  nodes: V2Node[];
  allDailyItems: Array<{
    item: DailyRechargeItem;
    periodIndex: number;
    itemIndex: number;
    globalIndex: number;
  }>;
}

/**
 * V2 节点类型
 */
export interface V2Node {
  id: number;
  type: "start" | "chest";
  required: number;
  amount?: number;
  claimed?: boolean;
  canClaim?: boolean;
  claimedToday?: boolean;
  userBonusTaskId?: string;
}

/**
 * 节点位置配置
 */
export interface NodePositionConfig {
  startLeft: number;
  endLeft: number;
  totalDistance: number;
  totalNodes: number;
}

/**
 * 将 jackpotPeriods 数据转换为 V2 进度数据
 */
export function convertJackpotPeriodsToV2Data(
  dailyRecharge?: DailyRechargeTask | null
): V2ProgressData {
  const jackpotPeriods = dailyRecharge?.dailyRechargeData?.jackpotPeriods;
  const totalValidDays = dailyRecharge?.dailyRechargeData?.totalValidDays || 28;

  if (!jackpotPeriods || jackpotPeriods.length === 0) {
    // 没有数据时返回默认结构
    return {
      currentProgress: 0,
      totalProgress: totalValidDays,
      nodes: [{ id: 0, type: "start" as const, required: 0 }],
      allDailyItems: [],
    };
  }

  // 展平所有 jackpotPeriods 下的 dailyRechargeItem
  const allDailyItems: V2ProgressData["allDailyItems"] = [];

  jackpotPeriods.forEach((period, periodIndex) => {
    period.dailyRechargeItem.forEach((item, itemIndex) => {
      allDailyItems.push({
        item,
        periodIndex,
        itemIndex,
        globalIndex: allDailyItems.length,
      });
    });
  });

  // 计算当前进度：从索引 0 开始，连续 claimed 为 true 的数量
  let currentProgress = 0;
  for (let i = 0; i < allDailyItems.length; i++) {
    if (allDailyItems[i].item.claimed) {
      currentProgress = i + 1;
    } else {
      break;
    }
  }

  // 构建节点数组
  const nodes: V2Node[] = [{ id: 0, type: "start" as const, required: 0 }];

  // 为每个周期创建一个宝箱节点
  jackpotPeriods.forEach((period, index) => {
    const periodDays = period.dailyRechargeItem.length;
    const requiredProgress = (index + 1) * periodDays;

    nodes.push({
      id: index + 1,
      type: "chest" as const,
      amount: period.jackpotReward,
      required: requiredProgress,
      claimed: period.claimed,
      canClaim: period.canClaim,
      claimedToday: period.claimedToday,
      userBonusTaskId: period.userBonusTaskId,
    });
  });

  return {
    currentProgress,
    totalProgress: totalValidDays,
    nodes,
    allDailyItems,
  };
}

/**
 * 获取节点位置配置
 */
export function getNodePositionConfig(totalNodes: number): NodePositionConfig {
  const startLeft = 1.8;
  const endLeft = 20.8;
  const totalDistance = endLeft - startLeft;

  return { startLeft, endLeft, totalDistance, totalNodes };
}

/**
 * 计算节点位置
 */
export function calculateNodePosition(
  index: number,
  config: NodePositionConfig
): number {
  const { startLeft, endLeft, totalNodes, totalDistance } = config;

  if (index === 0) return startLeft;
  if (index === totalNodes - 1) return endLeft;

  const middleSegments = totalNodes - 1;
  const segmentWidth = totalDistance / middleSegments;
  return startLeft + segmentWidth * index;
}

/**
 * 计算每个宝箱节点的小任务进度（x/7）
 */
export function getChestProgress(
  node: V2Node,
  currentProgress: number,
  nodes: V2Node[]
): number | null {
  if (node.type === "start") return null;

  const prevRequired = nodes[node.id - 1]?.required ?? 0;

  // 如果当前总进度已经达到或超过这个节点的要求
  if (currentProgress >= node.required) {
    return 7;
  }

  // 如果当前总进度还没到上一个节点
  if (currentProgress < prevRequired) {
    return 0;
  }

  // 在当前区间内，计算完成了几天
  return currentProgress - prevRequired;
}

/**
 * 判断节点的宝箱是否已打开
 */
export function isChestOpen(
  node: V2Node,
  claimedNodeId: number | null
): boolean {
  if (node.type === "start" || node.type !== "chest") return false;

  // 优先检查claimedNodeId（实时状态，用于动画期间）
  const isClaimed = claimedNodeId === node.id ? true : node.claimed;
  return !!isClaimed;
}

/**
 * 计算进度条填充宽度（百分比）
 */
export function calculateProgressBarWidth(currentProgress: number): number {
  // 定义每个周期的宽度区间（百分比）
  const periodRanges = [
    { start: 10, end: 22 }, // 第1周期（1-7天）
    { start: 33, end: 45 }, // 第2周期（8-14天）
    { start: 56, end: 68 }, // 第3周期（15-21天）
    { start: 78, end: 90 }, // 第4周期（22-28天）
  ];

  if (currentProgress === 0) {
    return periodRanges[0].start;
  }

  const periodIndex = Math.floor((currentProgress - 1) / 7);
  const maxPeriodIndex = periodRanges.length - 1;

  if (periodIndex >= periodRanges.length) {
    return periodRanges[maxPeriodIndex].end;
  }

  const currentRange = periodRanges[periodIndex];
  const rangeWidth = currentRange.end - currentRange.start;
  const dayInPeriod = ((currentProgress - 1) % 7) + 1;
  const progressInPeriod = (rangeWidth / 7) * dayInPeriod;

  return currentRange.start + progressInPeriod;
}

/**
 * 获取宝箱图片
 */
export function getChestImage(chestId: number, isOpen: boolean): string {
  const closeImages = [Chest01Close, Chest02Close, Chest03Close, Chest04Close];
  const openImages = [Chest01Open, Chest02Open, Chest03Open, Chest04Open];

  const index = chestId - 1;
  return isOpen ? openImages[index] : closeImages[index];
}

/**
 * 判断是否应该显示火焰动画
 */
export function shouldShowFireAnimation(
  currentProgress: number,
  nodeRequired: number,
  nodeClaimed: boolean
): boolean {
  return (
    currentProgress % 7 === 0 &&
    currentProgress === nodeRequired &&
    !nodeClaimed
  );
}

/**
 * 判断是否应该显示锁图标
 */
export function shouldShowLock(
  chestProgress: number | null,
  node: V2Node,
  nodes: V2Node[]
): boolean {
  // 如果进度不为0，不显示锁
  if (chestProgress !== 0) {
    return false;
  }

  // 如果是第一个宝箱节点，进度为0时显示锁
  if (node.id === 1) {
    return true;
  }

  // 对于后续的宝箱节点，如果进度为0，检查上一个周期大奖是否已领取
  const prevNode = nodes.find(n => n.id === node.id - 1);
  if (prevNode && prevNode.type === "chest") {
    // 如果上一个周期大奖已经领取，不显示锁
    if (prevNode.claimed) {
      return false;
    }
  }

  // 其他情况，进度为0时显示锁
  return true;
}

/**
 * 获取可领取的大奖节点
 */
export function getClaimableNode(
  isRecurringMode: boolean,
  dailyRecharge?: DailyRechargeTask | null
): V2Node | null {
  if (!isRecurringMode) return null;

  const jackpotPeriods = dailyRecharge?.dailyRechargeData?.jackpotPeriods;
  if (!jackpotPeriods || jackpotPeriods.length === 0) return null;

  console.log("jackpotPeriods", jackpotPeriods);

  for (let i = 0; i < jackpotPeriods.length; i++) {
    const period = jackpotPeriods[i];

    if (period.canClaim && !period.claimed) {
      return {
        id: i + 1,
        type: "chest" as const,
        amount: period.jackpotReward,
        required: (i + 1) * period.dailyRechargeItem.length,
        claimed: period.claimed,
        canClaim: period.canClaim,
        claimedToday: period.claimedToday,
        userBonusTaskId: period.userBonusTaskId,
      };
    }
  }

  return null;
}

/**
 * 判断是否刚领取完大奖
 * 包括手动领取和系统自动领取的情况
 */
export function isJustClaimedJackpot(
  isRecurringMode: boolean,
  dailyRecharge?: DailyRechargeTask | null
): boolean {
  if (!isRecurringMode) return false;

  const jackpotPeriods = dailyRecharge?.dailyRechargeData?.jackpotPeriods || [];
  if (!jackpotPeriods || jackpotPeriods.length === 0) return false;

  return jackpotPeriods.some(
    period =>
      // 手动领取且今天已领取
      (!period.canClaim && period.claimed && period.claimedToday) ||
      // 系统自动领取
      period.autoClaimed
  );
}

/**
 * 检测是否有系统自动领取的大奖（需要展示动画）
 * 场景：autoClaimed = true，说明是系统自动领取的
 */
export function hasAutoClaimedJackpot(
  isRecurringMode: boolean,
  dailyRecharge?: DailyRechargeTask | null
): V2Node | null {
  const jackpotPeriods = dailyRecharge?.dailyRechargeData?.jackpotPeriods || [];
  if (!jackpotPeriods || jackpotPeriods.length === 0) return null;

  // 查找 autoClaimed = true 的大奖
  for (let i = 0; i < jackpotPeriods.length; i++) {
    const period = jackpotPeriods[i];

    if (period.autoClaimed) {
      return {
        id: i + 1,
        type: "chest" as const,
        amount: period.jackpotReward,
        required: (i + 1) * period.dailyRechargeItem.length,
        claimed: period.claimed,
        canClaim: period.canClaim,
        claimedToday: period.claimedToday,
        userBonusTaskId: period.userBonusTaskId,
      };
    }
  }

  return null;
}

/**
 * 计算主宝箱状态
 */
export function calculateMainChestState(
  nodes: V2Node[],
  claimedNodeId: number | null,
  dailyRecharge?: DailyRechargeTask | null
): { chestIndex: number; isOpen: boolean } {
  const chestNodes = nodes.filter(n => n.type === "chest");
  const jackpotPeriods = dailyRecharge?.dailyRechargeData?.jackpotPeriods || [];

  // 点击领取时：显示点击的那个宝箱为 open
  if (claimedNodeId !== null) {
    const result = {
      chestIndex: claimedNodeId - 1,
      isOpen: true,
    };
    return result;
  }

  // 1. 优先检查是否有 autoClaimed = true 的大奖（系统自动领取）
  for (let i = 0; i < jackpotPeriods.length; i++) {
    if (jackpotPeriods[i].autoClaimed) {
      return {
        chestIndex: i,
        isOpen: true, // 系统自动领取的宝箱显示 open
      };
    }
  }

  // 2. 检查是否有手动领取且今天已领取的大奖（autoClaimed=false, claimed=true, claimedToday=true）
  for (let i = 0; i < jackpotPeriods.length; i++) {
    const period = jackpotPeriods[i];
    if (!period.autoClaimed && period.claimed && period.claimedToday) {
      return {
        chestIndex: i,
        isOpen: true, // 今天手动领取的宝箱显示 open
      };
    }
  }

  // 3. 找到第一个可以领取的宝箱（下一轮或当前未领取的），显示 close
  for (let i = 0; i < chestNodes.length; i++) {
    if (chestNodes[i].canClaim || !chestNodes[i].claimed) {
      const result = {
        chestIndex: i,
        isOpen: false,
      };
      return result;
    }
  }

  // 4. 找到最后一个未领取的大奖（备用逻辑）
  for (let i = chestNodes.length - 1; i >= 0; i--) {
    if (!chestNodes[i].claimed) {
      console.log("找到最后一个未领取的大奖（备用逻辑）");
      return {
        chestIndex: i,
        isOpen: false,
      };
    }
  }

  // 默认显示第一个宝箱，close
  const result = { chestIndex: 0, isOpen: false };
  console.log("走到默认显示第一个宝箱，close");
  return result;
}
